import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Play, Lock, Tag, ArrowLeft, Crown, Clock } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function LessonDetailPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/api/lessons/${encodeURIComponent(code)}`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setLesson(data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || 'Lesson not found');
          setLesson(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  // Inject Schema.org VideoObject JSON-LD so search engines can index
  // each lesson as a video result. Cleaned up on unmount/code change so
  // we don't leak stale tags between lessons.
  useEffect(() => {
    if (!lesson || !lesson.video_url) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: lesson.title,
      description: lesson.description || `Sudoku lesson ${lesson.code}`,
      thumbnailUrl: lesson.video_id
        ? [`https://i.ytimg.com/vi/${lesson.video_id}/maxresdefault.jpg`]
        : undefined,
      uploadDate: lesson.created_at,
      contentUrl: lesson.video_url,
      embedUrl: lesson.video_id
        ? `https://www.youtube.com/embed/${lesson.video_id}`
        : undefined,
      keywords: ['sudoku', 'tutorial', ...(lesson.tags || [])].join(', '),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [lesson]);

  const isPremium = ['premium', 'premium_plus'].includes(user?.subscription_tier);
  const canPlay = useMemo(() => {
    if (!lesson) return false;
    return isPremium || lesson.difficulty_level <= 3;
  }, [lesson, isPremium]);

  const startPlaying = async () => {
    if (!canPlay) {
      navigate('/subscription');
      return;
    }
    setStarting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/lessons/${encodeURIComponent(code)}/start`,
        {},
        { withCredentials: true }
      );
      navigate(`/play/${data.puzzle_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start lesson');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 py-12 text-center text-[var(--text-secondary)]">
          Loading lesson…
        </main>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 py-12 text-center">
          <p className="text-[var(--text-secondary)] mb-4">Lesson not found.</p>
          <Link to="/lessons" className="text-[#8B5A3C] hover:underline">
            Back to all lessons
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-main)' }}>
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/lessons')}
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-6"
          data-testid="back-to-lessons"
        >
          <ArrowLeft className="w-4 h-4" /> All lessons
        </button>

        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <Badge variant="outline" className="font-mono">
            {lesson.code}
          </Badge>
          <Badge variant="secondary">
            L{lesson.difficulty_level} · {lesson.difficulty_name}
          </Badge>
          {lesson.label && (
            <span className="text-xs text-[var(--text-disabled)] flex items-center gap-1">
              <Tag className="w-3 h-3" /> {lesson.label}
            </span>
          )}
          {lesson.created_at && (
            <span className="text-xs text-[var(--text-disabled)] flex items-center gap-1">
              <Clock className="w-3 h-3" /> {formatDate(lesson.created_at)}
            </span>
          )}
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[var(--text-primary)] mb-4">
          {lesson.title}
        </h1>

        {lesson.video_id ? (
          <div className="relative aspect-video w-full rounded-lg overflow-hidden mb-6 bg-black">
            <iframe
              title={lesson.title}
              src={`https://www.youtube.com/embed/${lesson.video_id}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="lesson-video"
            />
          </div>
        ) : (
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-6">
            <CardContent className="p-6 text-center text-[var(--text-secondary)]">
              No video attached to this lesson yet.
            </CardContent>
          </Card>
        )}

        {lesson.description && (
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-6">
            <CardContent className="p-5">
              <p className="text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">
                {lesson.description}
              </p>
            </CardContent>
          </Card>
        )}

        {lesson.technique_chain && lesson.technique_chain.length > 0 && (
          <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] mb-6">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-2">
                Techniques used in this puzzle
              </h3>
              <div className="flex flex-wrap gap-2">
                {lesson.technique_chain.map((t) => (
                  <Badge key={t} variant="secondary">
                    {t}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold text-[var(--text-primary)]">
              Play this puzzle
            </h3>
            <p className="text-sm text-[var(--text-secondary)]">
              {canPlay
                ? 'Opens the exact grid from the video. Your version is freshly transformed so it looks unique.'
                : 'Premium members can play the exact puzzle from the video.'}
            </p>
          </div>
          <Button
            onClick={startPlaying}
            disabled={starting}
            className={
              canPlay
                ? 'bg-[#8B5A3C] hover:bg-[#A46944] text-white px-6 py-5 text-base'
                : 'bg-[#AF52DE] hover:bg-[#C77BFF] text-white px-6 py-5 text-base'
            }
            data-testid="play-lesson-btn"
          >
            {canPlay ? (
              <span className="flex items-center gap-2">
                <Play className="w-5 h-5" /> {starting ? 'Starting…' : 'Start Puzzle'}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Crown className="w-5 h-5" /> Unlock Premium
              </span>
            )}
          </Button>
        </div>

        {!canPlay && (
          <p className="text-xs text-[var(--text-disabled)] mt-3 flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Free tier covers Levels 1–3. This lesson is Level {lesson.difficulty_level}.
          </p>
        )}
      </main>
    </div>
  );
}
